import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import supabase from '../supabase-client'
import { getISOTimestamp } from '../utils/dateUtils.js'

export default function LandingPage({ onAccessGranted }) {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')
  const [accessCode, setAccessCode] = useState('')
  const [showAccessCode, setShowAccessCode] = useState(false)

  // Check for magic access code in URL
  useState(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const code = urlParams.get('access') || urlParams.get('invite')
    if (code) {
      // You can define your magic codes here
      const validCodes = ['caledonia2025', 'earlyaccess', 'preview', 'demo']
      if (validCodes.includes(code.toLowerCase())) {
        onAccessGranted()
      }
    }
  }, [])

  const handleWaitlistSignup = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSuccess('')

    try {
      // Store waitlist email in a simple table or use a service like ConvertKit/Mailchimp
      // For now, we'll use Supabase to store waitlist emails
      const { error } = await supabase
        .from('waitlist')
        .insert([{ email, created_at: (() => getISOTimestamp())() }])

      if (error) {
        if (error.code === '23505') { // Unique constraint violation
          setError('You\'re already on our waitlist!')
        } else {
          setError('Something went wrong. Please try again.')
        }
      } else {
        setSuccess('Thanks! We\'ll notify you when Odin is ready for you.')
        setEmail('')
      }
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleAccessCode = (e) => {
    e.preventDefault()
    const validCodes = ['caledonia2025', 'earlyaccess', 'preview', 'demo']
    
    if (validCodes.includes(accessCode.toLowerCase())) {
      onAccessGranted()
    } else {
      setError('Invalid access code')
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-2xl space-y-8">
        
        {/* Hero Section */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <img src="/odinlogo.png" alt="Odin Logo" className="w-48 h-48 object-contain" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Odin is Coming Soon
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Revolutionary mushroom farm management software.<br />
            Built for modern mushroom producers.
          </p>
        </div>

        {/* Features Preview */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">📊 Smart Analytics</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                Track sales, customers, and performance with beautiful reports
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">🧾 Invoice Management</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                Generate professional invoices and track payments effortlessly
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">👥 Customer Hub</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                Manage customers, branches, and pricing all in one place
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Waitlist Form */}
        <Card className="mx-auto max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Join the Waitlist</CardTitle>
            <CardDescription>
              Be the first to know when Odin launches
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleWaitlistSignup} className="space-y-4">
              <div>
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                  {error}
                </div>
              )}
              
              {success && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded text-sm">
                  {success}
                </div>
              )}
              
              <Button type="submit" disabled={loading} className="w-full">
                {loading ? 'Joining...' : 'Join Waitlist'}
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              <Button
                type="button"
                variant="link"
                onClick={() => setShowAccessCode(!showAccessCode)}
                className="text-sm"
              >
                Have an access code?
              </Button>
            </div>
            
            {showAccessCode && (
              <form onSubmit={handleAccessCode} className="mt-4 space-y-3">
                <Input
                  type="text"
                  placeholder="Enter access code"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                />
                <Button type="submit" variant="outline" className="w-full">
                  Access Preview
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
        
        {/* Footer */}
        <div className="text-center text-sm text-gray-500">
          <p>© 2025 Caledonia Mushrooms. Built with care for mushroom farmers.</p>
        </div>
      </div>
    </div>
  )
}
